import { z } from 'zod';
import { agentProtocolSchema, agentTypeSchema } from '../enums/agent.js';
import { StatusTypeSchema } from '../enums/status.js';
import { AccountAuditSchema } from '../types/account.js';
import { agentProtocolDataSchema } from '../types/agent.protocol.js';
import {
  agentDetailsSchema,
  agentSkillSchema,
  customAgentSchema,
  csvAgentSchema,
  orchestrationAgentSchema,
  subAgentTableItemSchema,
} from '../types/agents.js';
import { ComplianceConfigSchema } from '../types/compliance.js';
import { UserAuditSchema } from '../types/user.js';
import { FilterSchema } from './filters.js';

export const agentsRecordSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  description: z.string(),
  status: StatusTypeSchema,
  protocol: agentProtocolSchema,
  typeId: agentTypeSchema,
  createdOn: z.coerce.date(),
  tags: z.array(z.string()),
});

export const getAgentsResponseSchema = z.object({
  items: z.array(agentsRecordSchema),
});

export const createAgentResponseSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  description: z.string(),
  protocolData: agentProtocolDataSchema,
  typeId: agentTypeSchema,
  createdOn: z.coerce.date(),
});

export const getAgentRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});

export const getAgentResponseSchema = agentDetailsSchema;

export const getAgentUsersResponseSchema = z.object({
  users: z.array(UserAuditSchema),
});

export const getAgentComplianceResponseSchema = z.object({
  complianceConfig: ComplianceConfigSchema.optional().nullable(),
});

export const getAgentAccountsResponseSchema = z.object({
  accounts: z.array(AccountAuditSchema),
});

export const updateAgentRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const updateAgentRequestBodySchema = z.union([
  customAgentSchema.omit({
    identifier: true,
    id: true,
    status: true,
    createdOn: true,
    skills: true,
    files: true,
    complianceConfig: true,
  }),
  csvAgentSchema.omit({
    identifier: true,
    id: true,
    status: true,
    createdOn: true,
    skills: true,
    files: true,
    complianceConfig: true,
  }),
  orchestrationAgentSchema.omit({
    identifier: true,
    id: true,
    status: true,
    createdOn: true,
    skills: true,
    files: true,
    complianceConfig: true,
  }),
]);
export const createAgentRequestBodySchema = updateAgentRequestBodySchema;

export const updateAgentStatusRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const updateAgentStatusRequestBodySchema = z.object({
  status: StatusTypeSchema,
});

export const deleteAgentRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});

export const createAgentSkillRequestBodySchema = z.object({
  skill: agentSkillSchema,
});
export const createAgentSkillRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});

export const deleteAgentSkillRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
  skillId: z.string(),
});

export const updateAgentSkillRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
  skillId: z.string(),
});
export const updateAgentSkillRequestBodySchema = z.object({
  skill: agentSkillSchema.omit({
    identifier: true,
  }),
});

export const addAgentSubAgentsRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const addAgentSubAgentsRequestBodySchema = z.object({
  subAgentIds: z.array(z.coerce.number()),
});

export const removeAgentSubAgentsRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const removeAgentSubAgentsRequestBodySchema = z.object({
  subAgentIds: z.array(z.coerce.number()),
});

export const getAgentSubAgentsRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const getAgentSubAgentsResponseBodySchema = z.object({
  items: z.array(subAgentTableItemSchema),
});

export const uploadAgentFileRequestParamsSchema = z.object({
  agentId: z.coerce.number(),
});
export const uploadAgentFileResponseSchema = z.object({
  file: z.object({
    id: z.number(),
    bucket: z.string(),
    key: z.string(),
    name: z.string(),
  }),
});

export namespace GetAgentDataSources {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
  });

  export type Params = z.infer<typeof paramsSchema>;

  export const responseSchema = z.object({
    dataSources: z.array(
      z.object({
        id: z.coerce.number(),
        name: z.string(),
      })
    ),
  });

  export type Response = z.infer<typeof responseSchema>;
}

export namespace AddAgentDataSources {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
    dataSourceId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({});

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace DeleteAgentDataSources {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
    dataSourceId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({
    dataSources: z.array(z.coerce.number()),
  });

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace GetAgentFilters {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
  });

  export type Params = z.infer<typeof paramsSchema>;

  export const responseSchema = z.object({
    filters: z.array(FilterSchema),
  });

  export type Response = z.infer<typeof responseSchema>;
}

export namespace AddAgentFilters {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
    filterId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({});

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace DeleteAgentFilters {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
    filterId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({});

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace UpdateAgentCompliance {
  export const paramsSchema = z.object({
    agentId: z.coerce.number(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const requestBodySchema = z.object({
    complianceConfig: ComplianceConfigSchema.optional().nullable(),
  });

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}

export namespace MassUpdateAgentTags {
  export const requestBodySchema = z.object({
    agentIds: z.array(z.coerce.number()),
    tags: z.array(z.string()),
  });

  export type RequestBody = z.infer<typeof requestBodySchema>;

  export const responseSchema = z.void();

  export type Response = z.infer<typeof responseSchema>;
}
